import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock } from "lucide-react";
import { Logo } from "@/components/Logo";
import { adminLogin, checkSession, formatApiError } from "@/admin/api";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [secret, setSecret] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkSession().then((ok) => ok && navigate("/admin", { replace: true }));
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await adminLogin(email, secret);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#050505] px-6" data-testid="admin-login-page">
      <div className="pointer-events-none absolute -top-32 left-1/2 h-96 w-[40rem] -translate-x-1/2 rounded-full bg-[#FF3333]/10 blur-[140px]" aria-hidden="true" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="glass w-full max-w-sm rounded-3xl p-8"
      >
        <div className="flex flex-col items-center">
          <Logo imgClassName="h-14 w-auto" />
          <p className="label-tech mt-3 text-[10px] text-neutral-500">Admin Portal</p>
        </div>
        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="admin-login-form">
          <div>
            <label htmlFor="admin-email" className="label-tech mb-2 block text-[10px] text-neutral-400">
              Admin email
            </label>
            <input
              id="admin-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@jomkonvoi.com"
              className="h-11 w-full rounded-xl border border-white/10 bg-white/5 px-4 text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:ring-2 focus:ring-[#FF3333]"
              data-testid="admin-email-input"
            />
          </div>
          <div>
            <label htmlFor="admin-secret" className="label-tech mb-2 block text-[10px] text-neutral-400">
              Portal secret
            </label>
            <input
              id="admin-secret"
              type="password"
              required
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder="••••••••••••"
              className="h-11 w-full rounded-xl border border-white/10 bg-white/5 px-4 text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:ring-2 focus:ring-[#FF3333]"
              data-testid="admin-secret-input"
            />
          </div>
          {error && (
            <p className="rounded-lg border border-[#FF3333]/40 bg-[#FF3333]/10 px-4 py-2.5 text-xs text-[#FF7a7a]" data-testid="admin-login-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            className="flex h-11 w-full items-center justify-center gap-2 rounded-full bg-[#FF3333] font-display text-sm font-bold text-white transition-colors duration-300 hover:bg-[#FF5C5C] disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-[#FF3333] focus:ring-offset-2 focus:ring-offset-black"
            data-testid="admin-login-submit"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
            Sign in
          </button>
        </form>
        <p className="mt-6 text-center text-[11px] leading-relaxed text-neutral-600">
          Authorised administrators only. Sessions expire after 12 hours.
        </p>
      </motion.div>
    </div>
  );
}
